import { useEffect, useState } from "react";

import {
  ALL_PRODUCT_TYPES,
  FORECAST_PRODUCT_LIST,
  PERFORMANCE_PRODUCT_LIST
} from "constants/chart.constants";
import {
  MY_FOCUS_FIELDS,
  ORG_FOCUS_FIELDS,
  ORG_GEO_FIELDS,
  ORG_GEO_SIMS,
  PROJECT_FOCUS_FIELDS,
  SHAPEFILE_FIELDS
} from "constants/settings.constants";
import { getProductType } from "utils";

import useFeatureFlags from "hooks/useFeatureFlags";

import { EntityKind } from "../../../models/entityKind";
import { useGroupByStore } from "../GroupByStoreProvider";
import {
  selectEntityKind,
  selectSelectedCategory,
  selectSelectedProduct
} from "../groupBySelectors";
import { geoVolumeCategoryTitle } from "./useCategories";

const useSelectedCategory = () => {
  const selectedCategory = useGroupByStore(selectSelectedCategory);
  const selectedProduct = useGroupByStore(selectSelectedProduct);
  const entityKind = useGroupByStore(selectEntityKind);
  const setSelectedProduct = useGroupByStore((s) => s.setSelectedProduct);
  const { hasFeature } = useFeatureFlags();

  const [value, setValue] = useState({
    hasPerformaceFields: false,
    hasForecastFields: false,
    hasProductFields: false,
    isUserDefinedColumn: false,
    isOrgDefinedColumn: false,
    isProjectDefinedColumn: false,
    isOrgGeoDefinedColumn: false,
    isOrgGeoSimsDefinedColumn: false,
    productList: [],
    isShapefileCategory: false,
    isGeoVolumeCategory: false,
    isPadMetricsCategory: false
  });

  useEffect(() => {
    if (!selectedCategory?.name) return;

    const { name } = selectedCategory;

    const hasPerformaceFields = name === "Performance";
    const hasForecastFields = name === "Forecast";
    const hasProductFields = hasPerformaceFields || hasForecastFields;
    const isGeoVolumeCategory = name === geoVolumeCategoryTitle;
    const isPadMetricsCategory = name === "Pad Metrics";

    const isUserDefinedColumn = name.indexOf(MY_FOCUS_FIELDS) >= 0;
    const isOrgDefinedColumn = name.indexOf(ORG_FOCUS_FIELDS) >= 0;
    const isProjectDefinedColumn = name.indexOf(PROJECT_FOCUS_FIELDS) >= 0;
    const isShapefileCategory = name.indexOf(SHAPEFILE_FIELDS) === 0;
    const isOrgGeoDefinedColumn = name.indexOf(ORG_GEO_FIELDS) >= 0;
    const isOrgGeoSimsDefinedColumn = name.indexOf(ORG_GEO_SIMS) >= 0;

    const productList = (
      hasPerformaceFields
        ? PERFORMANCE_PRODUCT_LIST
        : hasForecastFields
        ? FORECAST_PRODUCT_LIST
        : []
    )
      .filter((item) => !item.available || item.available(hasFeature))
      .map((item) => item.label);

    setValue({
      hasPerformaceFields,
      hasForecastFields,
      hasProductFields,
      isUserDefinedColumn,
      isOrgDefinedColumn,
      isProjectDefinedColumn,
      isOrgGeoDefinedColumn,
      isOrgGeoSimsDefinedColumn,
      productList,
      isShapefileCategory,
      isGeoVolumeCategory: isGeoVolumeCategory,
      isPadMetricsCategory
    });
  }, [selectedCategory?.name]);

  /**
   * Side-effect of category change:
   *
   * If selected-category has product list, and selected-product is not in the
   * product list, then set selected-product to the first product in the list.
   */
  useEffect(() => {
    const { hasProductFields, productList } = value;

    if (!hasProductFields || productList.length <= 1) return;

    const { label } = getProductType(selectedProduct);
    const productInList = selectedProduct && productList.includes(label);
    productInList ||
      setSelectedProduct(
        entityKind == EntityKind.Well
          ? productList.includes(ALL_PRODUCT_TYPES.Oil.label)
            ? ALL_PRODUCT_TYPES.Oil.label
            : productList[0]
          : productList.includes(ALL_PRODUCT_TYPES.GasInlet.label)
          ? ALL_PRODUCT_TYPES.GasInlet.label
          : productList[1]
      );
  }, [value.hasPerformaceFields, value.hasForecastFields, value.productList]);

  return value;
};

export default useSelectedCategory;
